import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  Pagination,
  Select,
  Spinner,
  Text,
  TextField
} from '@shopify/polaris';
import { accountingOperationsService } from '../services/accountingOperationsService';
import { ordersService } from '../services/ordersService';
import { authService } from '../services/authService';

const CARD_DEFINITIONS = [
  ['orders_placed', 'Orders Placed'],
  ['total_sales', 'Total Sales'],
  ['collected', 'Collected'],
  ['paid', 'Paid'],
  ['not_paid', 'Not Paid'],
  ['cancelled', 'Cancelled'],
  ['fees', 'Fees'],
  ['refunds', 'Refunds']
];

const toDateInput = (date) => date.toISOString().slice(0, 10);

const getDefaultRange = () => {
  const now = new Date();
  return {
    start_date: toDateInput(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))),
    end_date: toDateInput(now)
  };
};

const formatMoney = (amounts = []) => {
  if (amounts.length === 0) return '0';
  return amounts.map(({ amount, currency }) => (
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'EGP',
      maximumFractionDigits: 2
    }).format(Number(amount || 0))
  )).join(' · ');
};

const getCustomerName = (order) => (
  order.customer?.name
  || [order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ')
  || 'Unknown'
);

const getOrderAmount = (order) => Number(
  order.payment?.amount
  ?? order.payment?.total
  ?? order.payment?.total_amount
  ?? order.total_price
  ?? 0
);

const AccountingSummaryPage = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(getDefaultRange);
  const [cards, setCards] = useState({});
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [activeCard, setActiveCard] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setError('');
    try {
      const summaryResponse = await accountingOperationsService.getSummary(dateRange);
      setCards(summaryResponse.data?.cards || {});
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setSummaryLoading(false);
    }
  }, [dateRange]);

  const loadOrders = useCallback(async (page = 1) => {
    setOrdersLoading(true);
    setError('');
    try {
      const response = await ordersService.getOrders({
        ...dateRange,
        page,
        limit: 20,
        search,
        payment_method: paymentMethod,
        accounting_filter: activeCard || undefined,
        sort_by: 'created_at',
        sort_order: 'desc'
      });
      setOrders(response.data || []);
      setPagination(response.pagination || { page, totalPages: 1, total: 0 });
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setOrdersLoading(false);
    }
  }, [activeCard, dateRange, paymentMethod, search]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadOrders(1);
  }, [loadOrders]);

  const handleCardClick = (key) => {
    if (key === 'fees') return;
    setActiveCard(current => current === key ? '' : key);
  };

  const rows = useMemo(() => orders.map(order => {
    const currency = order.payment?.currency || order.currency || 'EGP';
    const paymentStatus = order.payment?.status || 'pending';
    const payoutCollected = Boolean(order.payment?.collected_at)
      || paymentStatus === 'collected'
      || paymentStatus === 'confirmed';
    return [
      <Button
        variant="plain"
        onClick={() => navigate(
          `/orders/${encodeURIComponent(order.order_id)}?country=${encodeURIComponent(
            authService.getActiveCountry()
          )}`
        )}
      >
        {order.order_id}
      </Button>,
      order.created_at ? new Date(order.created_at).toLocaleDateString() : '-',
      getCustomerName(order),
      order.payment?.method || '-',
      <Badge>{String(order.status || 'unknown').replaceAll('_', ' ')}</Badge>,
      <Badge tone={
        paymentStatus === 'collected'
          ? 'success'
          : paymentStatus === 'paid'
            ? 'info'
            : paymentStatus === 'refunded'
              ? 'warning'
              : 'attention'
      }>
        {String(paymentStatus).replaceAll('_', ' ')}
      </Badge>,
      payoutCollected ? <Badge tone="success">Collected</Badge> : <Badge>Pending</Badge>,
      new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(getOrderAmount(order)),
      new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
        Number(order.payment?.refunded_amount || 0)
      )
    ];
  }), [navigate, orders]);

  return (
    <BlockStack gap="400">
      {error && (
        <Banner
          tone="critical"
          action={{
            content: 'Retry',
            onAction: () => Promise.all([loadSummary(), loadOrders(pagination.page)])
          }}
        >
          {error}
        </Banner>
      )}

      <Card>
        <InlineStack gap="300" blockAlign="end">
          <TextField
            label="From"
            type="date"
            value={dateRange.start_date}
            onChange={(value) => setDateRange(current => ({ ...current, start_date: value }))}
            autoComplete="off"
          />
          <TextField
            label="To"
            type="date"
            value={dateRange.end_date}
            onChange={(value) => setDateRange(current => ({ ...current, end_date: value }))}
            autoComplete="off"
          />
        </InlineStack>
      </Card>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: '16px'
      }}>
        {CARD_DEFINITIONS.map(([key, label]) => {
            const metric = cards[key] || { count: 0, amounts: [] };
            const interactive = key !== 'fees';
            const headline = key === 'orders_placed'
              ? String(metric.count || 0)
              : formatMoney(metric.amounts);
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleCardClick(key)}
                disabled={!interactive}
                aria-pressed={activeCard === key}
                style={{
                  appearance: 'none',
                  border: activeCard === key ? '2px solid #2c6ecb' : '1px solid #dfe3e8',
                  borderRadius: '12px',
                  background: '#fff',
                  padding: '16px',
                  textAlign: 'left',
                  cursor: interactive ? 'pointer' : 'default',
                  opacity: 1
                }}
              >
                <BlockStack gap="200">
                  <Text as="span" variant="bodyMd" tone="subdued">{label}</Text>
                  <Text as="span" variant="headingLg">
                    {summaryLoading ? 'Loading…' : headline}
                  </Text>
                  {key !== 'orders_placed' && (
                    <Text as="span" variant="bodySm" tone="subdued">
                      {metric.count || 0} order{metric.count === 1 ? '' : 's'}
                    </Text>
                  )}
                </BlockStack>
              </button>
            );
          })}
        </div>

        <Card>
          <BlockStack gap="300">
            <InlineStack gap="300" blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Search orders"
                  value={searchInput}
                  onChange={setSearchInput}
                  clearButton
                  onClearButtonClick={() => setSearchInput('')}
                  autoComplete="off"
                  placeholder="Order, customer, phone, or email"
                />
              </div>
              <div style={{ minWidth: 210 }}>
                <Select
                  label="Payment method"
                  value={paymentMethod}
                  onChange={setPaymentMethod}
                  options={[
                    { label: 'All methods', value: '' },
                    { label: 'Online payment', value: 'online_payment' },
                    { label: 'Bosta / COD', value: 'cod' },
                    { label: 'InstaPay', value: 'instapay' },
                    { label: 'Mobile wallet', value: 'mobile_wallet' }
                  ]}
                />
              </div>
              {(activeCard || searchInput || paymentMethod) && (
                <Button onClick={() => {
                  setActiveCard('');
                  setSearchInput('');
                  setSearch('');
                  setPaymentMethod('');
                }}>
                  Clear filters
                </Button>
              )}
            </InlineStack>

            {ordersLoading ? (
              <InlineStack align="center"><Spinner accessibilityLabel="Loading accounting summary" /></InlineStack>
            ) : orders.length === 0 ? (
              <Text as="p" tone="subdued">
                {activeCard ? 'No orders match the selected summary card.' : 'No orders in this date range.'}
              </Text>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'numeric', 'numeric']}
                headings={[
                  'Order',
                  'Placed',
                  'Customer',
                  'Method',
                  'Order status',
                  'Payment',
                  'Payout',
                  'Total',
                  'Refunded'
                ]}
                rows={rows}
              />
            )}

            <InlineStack align="center">
              <Pagination
                hasPrevious={pagination.page > 1}
                onPrevious={() => loadOrders(pagination.page - 1)}
                hasNext={pagination.page < pagination.totalPages}
                onNext={() => loadOrders(pagination.page + 1)}
                label={`Page ${pagination.page || 1} of ${Math.max(pagination.totalPages || 1, 1)}`}
              />
            </InlineStack>
          </BlockStack>
        </Card>
    </BlockStack>
  );
};

export default AccountingSummaryPage;
